import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2, Save, Lock } from "lucide-react";

export default function CompanySettings() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", size: "", address: "", default_clock_in: "", default_clock_out: "", timezone: "", logo: "" });

  useEffect(() => {
    api.get("/company").then((r) => {
      setC(r.data);
      setForm({ name: r.data.name || "", industry: r.data.industry || "", size: r.data.size || "", address: r.data.address || "", default_clock_in: r.data.default_clock_in || "", default_clock_out: r.data.default_clock_out || "", timezone: r.data.timezone || "Africa/Lagos", logo: r.data.logo || "" });
    }).catch(() => {});
  }, []);

  const handleLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 650 * 1024) { toast.error("Image too large — pick one under 650KB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, logo: reader.result }));
    reader.readAsDataURL(f);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch("/company", form); setC(data); toast.success("Company details updated."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!c) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="company-settings">
      <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Company</h1></div>
      {!isSuper && (
        <div className="card-emerald p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="company-readonly-note">
          <Lock className="w-4 h-4 text-emerald-400 shrink-0" /> Only the Super Admin can edit company details. You're viewing them in read-only mode.
        </div>
      )}
      <div className="card-emerald p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="company-logo-row">
          {form.logo ? <img src={form.logo} alt="Logo" className="w-16 h-16 rounded-xl object-cover border border-emerald-500/30 shrink-0" /> : <div className="w-16 h-16 rounded-xl bg-emerald-500/15 grid place-items-center text-emerald-400 shrink-0"><Building2 className="w-6 h-6" /></div>}
          <div className="space-y-1 min-w-0">
            <Label>Company logo</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" accept="image/*" disabled={!isSuper} onChange={handleLogo} className="max-w-full text-xs text-muted-foreground file:mr-2 file:rounded-md file:border-0 file:bg-emerald-500 file:px-3 file:py-1.5 file:text-[#062217] file:font-semibold file:cursor-pointer" data-testid="company-logo-input" />
              {form.logo && isSuper && <button type="button" onClick={() => setForm({ ...form, logo: "" })} className="text-xs text-red-400" data-testid="company-logo-remove">Remove</button>}
            </div>
            <p className="text-[11px] text-muted-foreground">Shown in the sidebar and on payslips. PNG/JPG under 650KB.</p>
          </div>
        </div>
        <div className="sm:col-span-2"><Label>Company name</Label><Input disabled={!isSuper} value={form.name} onChange={set("name")} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-name-input" /></div>
        <div><Label>Industry</Label><Input disabled={!isSuper} value={form.industry} onChange={set("industry")} placeholder="e.g. Logistics" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-industry-input" /></div>
        <div><Label>Company size</Label><Input disabled={!isSuper} value={form.size} onChange={set("size")} placeholder="e.g. 21–50" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-size-input" /></div>
        <div className="sm:col-span-2"><Label>Address</Label><Input disabled={!isSuper} value={form.address} onChange={set("address")} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-address-input" /></div>
        <div><Label>Default clock-in time</Label><Input type="time" disabled={!isSuper} value={form.default_clock_in} onChange={set("default_clock_in")} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-clockin-input" /></div>
        <div><Label>Default clock-out time</Label><Input type="time" disabled={!isSuper} value={form.default_clock_out} onChange={set("default_clock_out")} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-clockout-input" /></div>
        <div className="sm:col-span-2"><Label>Timezone <span className="text-muted-foreground font-normal">(used for clock-in/out reminder emails)</span></Label><Input disabled={!isSuper} value={form.timezone} onChange={set("timezone")} placeholder="Africa/Lagos" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="company-timezone-input" /></div>
      </div>
      {isSuper && (
        <Button onClick={save} disabled={busy} className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="company-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save changes</>}
        </Button>
      )}
    </div>
  );
}
